import { equals, filter, find, map, pathOr, length, flatten, path } from "ramda";
import { MIN_PAGE_SIZE } from "src/constants";
import { OBJECT_META } from "src/services/constants";
import { getGlobalNamespace, getNamespaceName } from "src/services/objects/namespaces";
import { Metadata, TypeMeta } from "src/types";
import { isNotEmpty, returnIsNotEmptyOrTrue } from "src/utilities/hasValue";


interface IVarMap {
    id?: string;
    type_meta: TypeMeta;
    object_meta: any;
    metadata?: Metadata;
    variable_name: string;
    static_value?: any;
    value_path?: any;
    updated_at: string;
    created_at: string;
}


/**
 * VarMap class to manage varmap data
 */
class VarMap {
    varmap: IVarMap;
    constructor(varmap: IVarMap) {
        this.varmap = varmap
    }

    uid() {
        return pathOr('', ['id'], this.varmap)
    }

    namespace() {
        return pathOr('', [OBJECT_META, 'namespace'], this.varmap)
    }

    name() {
        return pathOr('', ['variable_name'], this.varmap)
    }

    staticValue() {
        return pathOr({}, ['static_value', 'value'], this.varmap)
    }

    valuePath() {
        return pathOr([], ['value_path'], this.varmap)
    }

    newVarMap(
        name: string,
        staticValue: any,
        valuePath: string[],
        namespace?: string
    ) {
        return new VarMap({
            variable_name: name,
            type_meta: {
                kind: 'VarMap',
                api_version: 'v1'
            },
            object_meta: {
                namespace: returnIsNotEmptyOrTrue(namespace, namespace, getGlobalNamespace())
            },
            metadata: {},
            static_value: returnIsNotEmptyOrTrue(staticValue, { "value": staticValue }, {}),
            value_path: returnIsNotEmptyOrTrue(valuePath, valuePath, []),
            created_at: 'now()',
            updated_at: 'now()'
        })
    }
}

/*
VarMapGroup is to handle the data of var maps by global and namespace
*/
class VarMapGroup {
    varmaps: VarMap[];

    constructor(varmaps: VarMap[]) {
        this.varmaps = varmaps
    }

    getVarMapsByNamespace(namespace: string) {
        return filter(
            (vrm: VarMap) => equals(namespace, vrm.namespace()),
            this.varmaps
        )
    }

    getVarMap(name: string, namespace?: string) {
       return find(
            (vrm: VarMap) => equals(name, vrm.name()),
            namespace ? this.getVarMapsByNamespace(namespace) : this.varmaps
        )
    }
}


/*
VarMapsHandler is to handle the data of varmaps
*/
class VarMapsHandler {
    varmapGroup: VarMapGroup;
    varmaps: IVarMap[];
    namespace: string;

    constructor(varmaps: IVarMap[]) {
        this.varmaps = varmaps;
        this.varmapGroup = new VarMapGroup(map((vr: any) => new VarMap(vr), varmaps))
        this.namespace = getNamespaceName()
    }

    numberOfPages(pageSize: number = MIN_PAGE_SIZE) {
        return length(this.varmaps) / pageSize
    }

    getVarMap(name: string, namespace?: string) {
        return this.varmapGroup.getVarMap(name, namespace)
    }

    prefixNamespace(name: string) {
        return `${this.namespace}_${name}`;
    }

    // getVarMapConfig get option value from template options
    // If template doesn't have options then try to pull from var map
    getVarMapConfig(varMapNames: string[], key: string = '') {
        const varmaps = filter(isNotEmpty, map((name: string) => this.getVarMap(name), varMapNames))
        return filter(isNotEmpty,
            flatten(
                [
                    map((varmap: VarMap) => key ? path([key], varmap.staticValue()) : varmap.staticValue(), varmaps)
                ]
            ))
    }

}

export {
    VarMapsHandler,
    VarMap,
    IVarMap
}